import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ModuleLayout from '../components/ModuleLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Image, Loader2, Instagram, Twitter, MessageCircle,
  Send, Copy, Trash2, RefreshCw, Clock, Hash, Pencil, Check, X,
  Megaphone, Plus, ChevronDown
} from 'lucide-react';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, color: 'from-pink-500 to-purple-600' },
  { value: 'twitter', label: 'Twitter / X', icon: Twitter, color: 'from-sky-400 to-blue-500' },
  { value: 'tiktok', label: 'TikTok', icon: Send, color: 'from-gray-800 to-gray-900' },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'from-green-500 to-emerald-600' },
];

const POST_TYPES = [
  { value: 'promo', labelKey: 'campaigns.types.promo' },
  { value: 'new_item', labelKey: 'campaigns.types.newItem' },
  { value: 'behind_scenes', labelKey: 'campaigns.types.behindScenes' },
  { value: 'seasonal', labelKey: 'campaigns.types.seasonal' },
  { value: 'brand', labelKey: 'campaigns.types.brand' },
];

function CampaignCard({ campaign, t, API, onDelete, onRegenImage, regenerating }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(campaign.content?.post_text || '');
  const platform = PLATFORMS.find(p => p.value === campaign.platform) || PLATFORMS[0];
  const PIcon = platform.icon;

  const handleCopy = () => {
    const text = campaign.content?.post_text || '';
    const tags = (campaign.content?.hashtags || []).join(' ');
    navigator.clipboard.writeText(`${text}\n\n${tags}`);
    toast.success(t('campaigns.copied'));
  };

  const handleSaveEdit = async () => {
    try {
      const { authHeaders } = window.__zouqAuth || {};
      await axios.put(`${API}/api/campaigns/${campaign.id}`, { post_text: editText }, authHeaders?.() || {});
      campaign.content.post_text = editText;
      setEditing(false);
      toast.success(t('campaigns.updated'));
    } catch { toast.error(t('campaigns.updateFailed')); }
  };

  return (
    <Card data-testid={`campaign-card-${campaign.id}`} className="bg-card border border-border/40 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
      {/* Image */}
      {campaign.image_url && (
        <div className="relative h-48 overflow-hidden group/img">
          <img
            src={campaign.image_url.startsWith('http') ? campaign.image_url : `${API}${campaign.image_url}`}
            alt=""
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <button
            onClick={() => onRegenImage(campaign.id)}
            disabled={regenerating === campaign.id}
            data-testid={`regen-img-${campaign.id}`}
            className="absolute bottom-2 start-2 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 hover:bg-black/80 text-white text-[10px] font-medium transition-all opacity-0 group-hover/img:opacity-100 disabled:opacity-70"
          >
            {regenerating === campaign.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {t('campaigns.regenImage')}
          </button>
          <div className={`absolute top-2 end-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r ${platform.color} text-white text-[10px] font-bold`}>
            <PIcon className="w-3 h-3" /> {platform.label}
          </div>
        </div>
      )}

      {!campaign.image_url && (
        <div className={`h-20 bg-gradient-to-r ${platform.color} flex items-center justify-center gap-2`}>
          <PIcon className="w-6 h-6 text-white/80" />
          <span className="text-white/90 font-semibold text-sm">{platform.label}</span>
        </div>
      )}

      <CardContent className="p-4 space-y-3">
        {/* Post Text */}
        {editing ? (
          <div className="space-y-2">
            <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={4} className="text-sm" dir="rtl" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveEdit} className="text-xs"><Check className="w-3 h-3 me-1" />{t('common.save')}</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditText(campaign.content?.post_text || ''); }} className="text-xs"><X className="w-3 h-3" /></Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap line-clamp-5" dir="rtl" data-testid={`campaign-text-${campaign.id}`}>
            {campaign.content?.post_text}
          </p>
        )}

        {/* Hashtags */}
        {campaign.content?.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {campaign.content.hashtags.map((tag, i) => (
              <span key={i} className="text-[11px] text-primary/80 bg-primary/5 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}

        {/* Suggested Time */}
        {campaign.content?.suggested_time && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" /> {campaign.content.suggested_time}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border/30">
          <span className="text-[10px] text-muted-foreground">
            {new Date(campaign.created_at).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleCopy} data-testid={`copy-campaign-${campaign.id}`} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground">
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(campaign.id)} data-testid={`delete-campaign-${campaign.id}`} className="h-7 w-7 p-0 text-destructive hover:text-destructive">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MarketingCampaignsPage() {
  const { authHeaders, API, user } = useAuth();
  const { t } = useTranslation();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [platform, setPlatform] = useState('instagram');
  const [postType, setPostType] = useState('promo');
  const [customTopic, setCustomTopic] = useState('');
  const [withImage, setWithImage] = useState(true);

  // Expose authHeaders for inline edit
  useEffect(() => { window.__zouqAuth = { authHeaders }; return () => { delete window.__zouqAuth; }; }, [authHeaders]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/list`, authHeaders());
      setCampaigns(res.data.campaigns || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [API, authHeaders]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/campaigns/generate`, {
        platform, post_type: postType, custom_topic: customTopic, generate_image: withImage,
      }, authHeaders());
      if (res.data.success) {
        toast.success(t('campaigns.generated'));
        setShowForm(false);
        setCustomTopic('');
        fetchCampaigns();
      }
    } catch (err) {
      toast.error(t('campaigns.generateFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/campaigns/${id}`, authHeaders());
      setCampaigns(prev => prev.filter(c => c.id !== id));
      toast.success(t('campaigns.deleted'));
    } catch { toast.error(t('campaigns.deleteFailed')); }
  };

  const handleRegenImage = async (id) => {
    setRegenerating(id);
    try {
      const res = await axios.post(`${API}/campaigns/${id}/regenerate-image`, {}, authHeaders());
      if (res.data.success) {
        toast.success(t('campaigns.imageRegenerated'));
        fetchCampaigns();
      }
    } catch { toast.error(t('campaigns.imageRegenFailed')); }
    finally { setRegenerating(null); }
  };

  const selectedPlatform = PLATFORMS.find(p => p.value === platform) || PLATFORMS[0];

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-6" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-cairo font-bold text-2xl text-foreground flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-primary" />
              {t('campaigns.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('campaigns.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="new-campaign-btn"
            className="rounded-full gap-2"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? t('common.cancel') : t('campaigns.newCampaign')}
          </Button>
        </div>

        {/* Generation Form */}
        {showForm && (
          <Card data-testid="campaign-form" className="bg-card border-primary/20 border-2 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 py-3 border-b border-primary/10">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                {t('campaigns.generateTitle')}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t('campaigns.generateSubtitle')}</p>
            </div>
            <CardContent className="p-5 space-y-4">
              {/* Platform Selection */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">{t('campaigns.platform')}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PLATFORMS.map(p => {
                    const Icon = p.icon;
                    const active = platform === p.value;
                    return (
                      <button
                        key={p.value}
                        onClick={() => setPlatform(p.value)}
                        data-testid={`platform-${p.value}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                          active
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-border hover:text-foreground'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Post Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('campaigns.postType')}</label>
                  <Select value={postType} onValueChange={setPostType}>
                    <SelectTrigger data-testid="post-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {POST_TYPES.map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{t(pt.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t('campaigns.customTopic')}</label>
                  <Input
                    value={customTopic}
                    onChange={e => setCustomTopic(e.target.value)}
                    placeholder={t('campaigns.topicPlaceholder')}
                    data-testid="custom-topic-input"
                    dir="rtl"
                  />
                </div>
              </div>

              {/* Image Toggle + Generate */}
              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={withImage}
                    onChange={e => setWithImage(e.target.checked)}
                    data-testid="with-image-toggle"
                    className="rounded border-border"
                  />
                  <Image className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{t('campaigns.generateWithImage')}</span>
                </label>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-campaign-btn"
                  className="rounded-full gap-2 min-w-[160px]"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('campaigns.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {t('campaigns.generate')}
                    </>
                  )}
                </Button>
              </div>

              {generating && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{t('campaigns.generatingHint')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Campaigns Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-lg font-medium">{t('campaigns.empty')}</p>
            <p className="text-sm mt-1">{t('campaigns.emptyHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {campaigns.map(c => (
              <CampaignCard
                key={c.id}
                campaign={c}
                t={t}
                API={API}
                onDelete={handleDelete}
                onRegenImage={handleRegenImage}
                regenerating={regenerating}
              />
            ))}
          </div>
        )}
      </div>
    </ModuleLayout>
  );
}
